import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, ShoppingCart, Lock, UserPlus, TrendingUp, CalendarDays, Shield, GraduationCap } from 'lucide-react';

export interface BotService {
  id: string;
  icon: React.ReactNode;
  complexity: 'Low' | 'Medium' | 'High';
  priceMin: number;
  priceMax: number;
}

// Synced with bot/catalog.ts SERVICES (2026 market pricing).
export const botServices: BotService[] = [
  { id: 'ai-support', icon: <Bot size={22} />, complexity: 'Medium', priceMin: 25000, priceMax: 75000 },
  { id: 'ecommerce', icon: <ShoppingCart size={22} />, complexity: 'Medium', priceMin: 30000, priceMax: 100000 },
  { id: 'subscription', icon: <Lock size={22} />, complexity: 'Low', priceMin: 20000, priceMax: 60000 },
  { id: 'lead-gen', icon: <UserPlus size={22} />, complexity: 'Low', priceMin: 20000, priceMax: 60000 },
  { id: 'trading', icon: <TrendingUp size={22} />, complexity: 'High', priceMin: 35000, priceMax: 115000 },
  { id: 'booking', icon: <CalendarDays size={22} />, complexity: 'Medium', priceMin: 25000, priceMax: 85000 },
  { id: 'moderation', icon: <Shield size={22} />, complexity: 'Medium', priceMin: 15000, priceMax: 45000 },
  { id: 'education', icon: <GraduationCap size={22} />, complexity: 'Medium', priceMin: 30000, priceMax: 100000 },
];

const complexityConfig = {
  Low: { color: '#00E5A0', bg: 'rgba(0,229,160,0.12)' },
  Medium: { color: '#FFB020', bg: 'rgba(255,176,32,0.12)' },
  High: { color: '#FF4757', bg: 'rgba(255,71,87,0.12)' },
};

interface ServicesSectionProps {
  selectedService: string | null;
  onSelectService: (id: string) => void;
}

export default function ServicesSection({ selectedService, onSelectService }: ServicesSectionProps) {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-fade-in-up');
                el.classList.remove('opacity-0');
              }, i * 80);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="services" className="py-12 sm:py-16 lg:py-20 px-5 scroll-mt-16">
      <div className="max-w-lg sm:max-w-3xl lg:max-w-6xl mx-auto">
        {/* Header */}
        <div className="reveal opacity-0 mb-6 lg:mb-10 lg:text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 glass-pill rounded-full mb-3">
            <span className="w-1 h-1 rounded-full bg-[#00D4FF]" />
            <p className="text-[#00D4FF] text-[10px] font-semibold uppercase tracking-[0.12em]">{t('services.label')}</p>
          </div>
          <h2 className="text-[#F0F2F5] text-2xl sm:text-3xl lg:text-4xl font-bold">{t('services.title')}</h2>
        </div>

        {/* Mobile: horizontal scroll · md+: grid */}
        <div className="flex gap-3.5 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-5 px-5
                        md:grid md:grid-cols-2 md:overflow-visible md:px-0 md:mx-0 md:pb-0
                        lg:grid-cols-3 xl:grid-cols-4 md:gap-4 lg:gap-5">
          {botServices.map((service) => {
            const isSelected = selectedService === service.id;
            const comp = complexityConfig[service.complexity];

            return (
              <div
                key={service.id}
                onClick={() => onSelectService(service.id)}
                className={`reveal opacity-0 flex-shrink-0 w-[265px] snap-start rounded-2xl p-5 cursor-pointer
                  md:w-auto md:flex-shrink md:snap-none
                  ${isSelected ? 'glass-card-active' : 'glass-card'}`}
              >
                {/* Icon */}
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.15) 0%, rgba(0,212,255,0.05) 100%)', color: '#00D4FF' }}>
                  {service.icon}
                </div>

                {/* Title */}
                <h3 className="text-[#F0F2F5] text-[15px] font-semibold mb-1.5">
                  {t(`services.bots.${service.id}.name`)}
                </h3>

                {/* Description */}
                <p className="text-[#8A93A7] text-[11px] leading-relaxed mb-3 line-clamp-2">
                  {t(`services.bots.${service.id}.desc`)}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {([0, 1, 2, 3] as const).map((i) => (
                    <span key={i} className="text-[11px] text-[#B5BBC9] bg-[rgba(138,147,167,0.1)] px-2 py-0.5 rounded">
                      {t(`services.bots.${service.id}.features.${i}`)}
                    </span>
                  ))}
                </div>

                {/* Complexity badge */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full" style={{ background: comp.bg, color: comp.color }}>
                    {t(`services.complexity${service.complexity}`)}
                  </span>
                </div>

                {/* Price */}
                <p className="text-[#00D4FF] font-bold text-sm mb-3">от {service.priceMin.toLocaleString('ru-RU')} ₽</p>

                {/* Select button */}
                <button
                  onClick={(e) => { e.stopPropagation(); onSelectService(service.id); }}
                  className={`w-full h-9 rounded-lg text-xs font-semibold transition-all duration-200
                    ${isSelected
                      ? 'glass-btn text-[#0B0E14]'
                      : 'glass-btn-secondary text-[#00D4FF]'
                    }`}
                >
                  {isSelected ? t('services.selected') : t('services.select')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
